/**
 * Consultation Detail Card - Display consultation details
 */
import React from 'react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  Download, 
  Trash2, 
  Edit, 
  Printer, 
  Eye,
  Sun,
  Sunset,
  Moon,
  Calendar,
  X
} from 'lucide-react';
import { getValueColor } from './utils';
import { cn } from '../../lib/utils';

export const ConsultationDetailCard = ({
  consultation,
  files,
  diagnoses,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onPrint,
  onClose,
  onPreviewFile,
  onDownloadFile,
  onDeleteFile,
  theme
}) => {
  if (!consultation) return null;

  const getDiagnosisName = (diagId) => {
    const diagnosis = diagnoses.find(d => d.id === diagId);
    if (diagnosis) {
      return `${diagnosis.code}: ${diagnosis.name}`;
    }
    return diagId;
  };

  return (
    <Card data-testid="consultation-detail-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Consultație din {format(new Date(consultation.consultation_date), 'd MMMM yyyy', { locale: ro })}
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Ora: {consultation.consultation_time} | Dr. {consultation.doctor_name}
          </p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button 
              size="sm" 
              onClick={onEdit}
              className={cn(theme?.primaryBg, theme?.primaryBgHover)}
              data-testid="edit-consultation-btn"
            >
              <Edit className="w-4 h-4 mr-1" /> Editează
            </Button>
          )}
          <Button 
            size="sm" 
            onClick={onPrint}
            className={cn(theme?.primaryBg, theme?.primaryBgHover)}
            data-testid="print-consultation-btn"
          >
            <Printer className="w-4 h-4 mr-1" /> Printează
          </Button>
          {canDelete && (
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={onDelete}
              data-testid="delete-consultation-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            data-testid="close-consultation-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Diagnoses */}
        {consultation.diagnosis_ids?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-2">Diagnostice</p>
            <div className="flex flex-wrap gap-2">
              {consultation.diagnosis_ids.map((diagId) => (
                <Badge key={diagId} variant="secondary">
                  {getDiagnosisName(diagId)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Lab Results */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 p-4 bg-slate-50 rounded-lg">
          {consultation.glycemia && (
            <div>
              <p className="text-xs text-slate-500">Glicemie</p>
              <p className={`font-medium ${getValueColor('glycemia', consultation.glycemia)}`}>
                {consultation.glycemia} mg/dL
              </p>
            </div>
          )}
          {consultation.hba1c && (
            <div>
              <p className="text-xs text-slate-500">HbA1c</p>
              <p className={`font-medium ${getValueColor('hba1c', consultation.hba1c)}`}>
                {consultation.hba1c}%
              </p>
            </div>
          )}
          {consultation.blood_pressure && (
            <div>
              <p className="text-xs text-slate-500">TA</p>
              <p className="font-medium">{consultation.blood_pressure} mmHg</p>
            </div>
          )}
          {consultation.weight && (
            <div>
              <p className="text-xs text-slate-500">Greutate</p>
              <p className="font-medium">{consultation.weight} kg</p>
            </div>
          )}
          {consultation.hdl && (
            <div>
              <p className="text-xs text-slate-500">HDL</p>
              <p className={`font-medium ${getValueColor('hdl', consultation.hdl)}`}>
                {consultation.hdl} mg/dL
              </p>
            </div>
          )}
          {consultation.ldl && (
            <div>
              <p className="text-xs text-slate-500">LDL</p>
              <p className={`font-medium ${getValueColor('ldl', consultation.ldl)}`}>
                {consultation.ldl} mg/dL
              </p>
            </div>
          )}
          {consultation.triglycerides && (
            <div>
              <p className="text-xs text-slate-500">Trigliceride</p>
              <p className={`font-medium ${getValueColor('triglycerides', consultation.triglycerides)}`}>
                {consultation.triglycerides} mg/dL
              </p>
            </div>
          )}
        </div>

        {/* Other lab results */}
        {consultation.other_lab_results && (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">Alte analize</p>
            <p className="text-slate-700 whitespace-pre-wrap">{consultation.other_lab_results}</p>
          </div>
        )}

        {/* Symptoms */}
        {consultation.symptoms && (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">Simptome</p>
            <p className="text-slate-700">{consultation.symptoms}</p>
          </div>
        )}

        {/* Clinical exam */}
        {consultation.clinical_exam && (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">Examen clinic</p>
            <p className="text-slate-700">{consultation.clinical_exam}</p>
          </div>
        )}

        {/* Medications */}
        {consultation.medications?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-2">Medicamente prescrise</p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 font-medium">Denumire</th>
                    <th className="text-left p-2 font-medium">DCI</th>
                    <th className="text-left p-2 font-medium">Formă / Doză</th>
                    <th className="text-left p-2 font-medium">DS</th>
                    <th className="text-center p-2 font-medium">Zile</th>
                    <th className="text-center p-2 font-medium w-10">
                      <Sun className="w-3 h-3 mx-auto text-amber-500" />
                    </th>
                    <th className="text-center p-2 font-medium w-10">
                      <Sunset className="w-3 h-3 mx-auto text-orange-500" />
                    </th>
                    <th className="text-center p-2 font-medium w-10">
                      <Moon className="w-3 h-3 mx-auto text-indigo-500" />
                    </th>
                    <th className="text-center p-2 font-medium w-10">
                      <Calendar className="w-3 h-3 mx-auto text-purple-500" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {consultation.medications.map((med, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-2 font-medium">{med.commercial_name}</td>
                      <td className="p-2 text-slate-600" title={med.inn_name}>
                        {med.inn_name?.length > 15 ? `${med.inn_name.substring(0, 15)}...` : med.inn_name}
                      </td>
                      <td className="p-2 text-slate-600" title={`${med.form} - ${med.dose}`}>
                        {`${med.form} - ${med.dose}`.length > 30 
                          ? `${`${med.form} - ${med.dose}`.substring(0, 30)}...` 
                          : `${med.form} - ${med.dose}`}
                      </td>
                      <td className="p-2 text-slate-600">{med.ds || '-'}</td>
                      <td className="p-2 text-center">{med.treatment_days || '-'}</td>
                      <td className="p-2 text-center">
                        {med.morning && <span className="text-emerald-600 font-bold">✓</span>}
                      </td>
                      <td className="p-2 text-center">
                        {med.lunch && <span className="text-emerald-600 font-bold">✓</span>}
                      </td>
                      <td className="p-2 text-center">
                        {med.evening && <span className="text-emerald-600 font-bold">✓</span>}
                      </td>
                      <td className="p-2 text-center">
                        {med.once_weekly && <span className="text-purple-600 font-bold">✓</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {consultation.recommendations && (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">Recomandări</p>
            <p className="text-slate-700">{consultation.recommendations}</p>
          </div>
        )}

        {/* Files */}
        {files?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-2">Fișiere atașate</p>
            <div className="flex flex-wrap gap-2">
              {files.map((file) => (
                <div 
                  key={file.id} 
                  className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg"
                >
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="text-sm truncate max-w-[150px]">{file.filename}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => onPreviewFile(file)}
                    data-testid={`preview-file-${file.id}`}
                  >
                    <Eye className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => onDownloadFile(file)}
                    data-testid={`download-file-${file.id}`}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      onClick={() => onDeleteFile(file.id)}
                      data-testid={`delete-file-${file.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
